import React, { useState, useEffect } from 'react';
import { 
  Shield, CheckCircle, Clock, Phone, FileText, Sun, 
  Battery, Zap, Wrench, AlertTriangle, ChevronDown,
  Award, Calendar, Building2, Home, MessageCircle, ArrowRight
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const GarantiasPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const warranties = [
    {
      icon: Sun,
      title: 'Paneles Solares',
      productWarranty: '12 años',
      performanceWarranty: '25 años',
      performanceDetail: 'Mínimo 80% de eficiencia',
      brands: ['Longi', 'JA Solar', 'Trina Solar', 'Canadian Solar'],
      coverage: [
        'Defectos de fabricación',
        'Delaminación y corrosión',
        'Fallas en células fotovoltaicas',
        'Problemas en la caja de conexiones'
      ],
      color: 'emerald'
    },
    {
      icon: Zap,
      title: 'Inversores',
      productWarranty: '5-10 años',
      performanceWarranty: 'Según modelo',
      performanceDetail: 'Extendible hasta 20 años',
      brands: ['Huawei', 'Growatt', 'Fronius', 'SMA'],
      coverage: [
        'Defectos de fabricación',
        'Fallas electrónicas',
        'Problemas de software',
        'Daños internos por voltaje'
      ],
      color: 'blue'
    },
    {
      icon: Battery,
      title: 'Baterías',
      productWarranty: '10 años',
      performanceWarranty: '10 años / 6000 ciclos',
      performanceDetail: 'Lo que ocurra primero',
      brands: ['BYD', 'Pylontech', 'Huawei Luna'],
      coverage: [
        'Defectos de fabricación',
        'Degradación prematura',
        'Fallas en el BMS',
        'Problemas de capacidad'
      ],
      color: 'purple'
    },
    {
      icon: Building2,
      title: 'Estructuras',
      productWarranty: '10 años',
      performanceWarranty: '25 años',
      performanceDetail: 'Resistencia estructural',
      brands: ['Aluminio anodizado', 'Acero galvanizado'],
      coverage: [
        'Corrosión prematura',
        'Defectos de soldadura',
        'Fallas en anclajes',
        'Deformaciones estructurales'
      ],
      color: 'zinc'
    },
    {
      icon: Wrench,
      title: 'Instalación',
      productWarranty: '5 años',
      performanceWarranty: 'Mano de obra',
      performanceDetail: 'Garantía Zenex',
      brands: ['Certificación propia'],
      coverage: [
        'Defectos en montaje',
        'Problemas de cableado',
        'Filtraciones por instalación',
        'Errores de conexión'
      ],
      color: 'amber'
    }
  ];

  const warrantyProcess = [
    {
      step: 1,
      title: 'Reportar el Problema',
      description: 'Contactanos por WhatsApp, teléfono o email describiendo el problema detectado.',
      icon: MessageCircle,
      time: 'Inmediato'
    },
    {
      step: 2,
      title: 'Evaluación Inicial',
      description: 'Nuestro equipo evalúa el caso y determina si corresponde a garantía.',
      icon: FileText,
      time: '24-48 horas'
    },
    {
      step: 3,
      title: 'Diagnóstico Técnico',
      description: 'Visita técnica para diagnosticar el problema y documentar la falla.',
      icon: Wrench,
      time: '3-5 días'
    },
    {
      step: 4,
      title: 'Resolución',
      description: 'Reparación o reemplazo del componente afectado sin costo.',
      icon: CheckCircle,
      time: '5-15 días'
    }
  ];

  const notCovered = [
    'Daños por descargas eléctricas o rayos',
    'Manipulación por personal no autorizado',
    'Daños por eventos climáticos extremos (huracanes, terremotos)',
    'Falta de mantenimiento según recomendaciones',
    'Daños causados por animales o plagas',
    'Modificaciones no autorizadas al sistema',
    'Desgaste normal de componentes fuera del período de garantía'
  ];

  const faqs = [
    {
      question: '¿Cómo hago un reclamo de garantía?',
      answer: 'Contactanos por WhatsApp al +595 991 344956 o por email a zenexparaguay@gmail.com con tu número de cliente y descripción del problema. Responderemos en 24-48 horas hábiles.'
    },
    {
      question: '¿La garantía cubre la mano de obra?',
      answer: 'Sí, durante los primeros 5 años la garantía de instalación cubre la mano de obra para reparaciones relacionadas con defectos de instalación. Para reclamos de garantía de producto (paneles, inversores), la mano de obra está incluida.'
    },
    {
      question: '¿Qué pasa si el fabricante cierra?',
      answer: 'Trabajamos con marcas de primer nivel con presencia global. Además, Zenex actúa como respaldo y se hace cargo de las garantías incluso si hubiera problemas con algún fabricante.'
    },
    {
      question: '¿La garantía es transferible?',
      answer: 'Sí, si vendés tu propiedad, la garantía se transfiere al nuevo propietario. Solo necesitás notificarnos el cambio de titularidad para actualizar nuestros registros.'
    },
    {
      question: '¿Puedo extender la garantía del inversor?',
      answer: 'Sí, ofrecemos extensiones de garantía para inversores de hasta 20 años. El costo depende del modelo y el período de extensión. Consultá las opciones disponibles.'
    },
    {
      question: '¿Qué documentos necesito para un reclamo?',
      answer: 'Necesitás: número de cliente o contrato, fotos del problema, descripción detallada de la falla, y cualquier mensaje de error que muestre el sistema de monitoreo.'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'bg-emerald-100 text-emerald-600',
      blue: 'bg-blue-100 text-blue-600',
      purple: 'bg-purple-100 text-purple-600',
      zinc: 'bg-zinc-200 text-zinc-600',
      amber: 'bg-amber-100 text-amber-600'
    };
    return colors[color] || colors.emerald;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="garantias-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-emerald-800 via-emerald-700 to-teal-800 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-emerald-300 mb-6">
            <Shield className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Respaldo Total</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Garantías
            <span className="block text-emerald-300">Tu Inversión Protegida</span>
          </h1>
          
          <p className="text-xl text-emerald-100 max-w-2xl mb-8">
            Trabajamos con las mejores marcas del mundo y respaldamos cada instalación 
            con garantías completas que protegen tu inversión por décadas.
          </p>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              { value: '25 años', label: 'Garantía paneles' },
              { value: '10 años', label: 'Garantía inversores' },
              { value: '5 años', label: 'Garantía instalación' },
              { value: '24/7', label: 'Soporte técnico' }
            ].map((stat, index) => (
              <div key={index} className="text-center p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                <p className="text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-emerald-200">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warranties Detail */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Detalle de Garantías por Componente
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Cada componente de tu sistema solar cuenta con garantías específicas de fabricante y de instalación.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {warranties.map((warranty, index) => {
              const Icon = warranty.icon;
              return (
                <div 
                  key={index}
                  className="p-6 bg-zinc-50 rounded-2xl hover:shadow-lg transition-all border border-zinc-100"
                >
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${getColorClasses(warranty.color)}`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-zinc-900 mb-4">{warranty.title}</h3>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between items-center py-2 border-b border-zinc-200">
                      <span className="text-zinc-500 text-sm">Garantía de producto</span>
                      <span className="font-bold text-emerald-600">{warranty.productWarranty}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-zinc-200">
                      <span className="text-zinc-500 text-sm">Garantía rendimiento</span>
                      <span className="font-bold text-zinc-900">{warranty.performanceWarranty}</span>
                    </div>
                    <p className="text-xs text-zinc-400">{warranty.performanceDetail}</p>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs text-zinc-500 mb-2">Marcas:</p>
                    <div className="flex flex-wrap gap-1">
                      {warranty.brands.map((brand, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-zinc-200 text-zinc-600 text-xs rounded">
                          {brand}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-zinc-500 mb-2">Cobertura incluye:</p>
                    <ul className="space-y-1">
                      {warranty.coverage.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-xs text-zinc-600">
                          <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Warranty Process */}
      <section className="py-20 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Proceso de Reclamo de Garantía
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Hacemos que el proceso sea simple y rápido para que vuelvas a disfrutar de tu sistema cuanto antes.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {warrantyProcess.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="text-center relative">
                  {index < warrantyProcess.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-emerald-600/30"></div>
                  )}
                  <div className="relative z-10 w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8" />
                  </div>
                  <div className="text-emerald-400 text-sm font-medium mb-2">{step.time}</div>
                  <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                  <p className="text-zinc-400 text-sm">{step.description}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <a 
              href="https://wa.me/595991344956?text=Hola,%20necesito%20hacer%20un%20reclamo%20de%20garantía"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Iniciar Reclamo de Garantía
            </a>
          </div>
        </div>
      </section>

      {/* Not Covered Section */}
      <section className="py-16 bg-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-zinc-900 mb-4">
                Exclusiones de Garantía
              </h2>
              <p className="text-zinc-600 mb-6">
                Para mantener la validez de tus garantías, tené en cuenta que no están cubiertos:
              </p>
              <ul className="grid md:grid-cols-2 gap-3">
                {notCovered.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-zinc-700">
                    <span className="text-amber-500 mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-zinc-900 mb-4">
              Preguntas sobre Garantías
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-zinc-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-zinc-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 mx-auto text-white/80 mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">
            ¿Necesitás asistencia técnica?
          </h2>
          <p className="text-emerald-100 mb-8">
            Nuestro equipo de soporte está disponible para ayudarte
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://wa.me/595991344956"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-white text-emerald-600 font-semibold rounded-xl hover:bg-emerald-50 transition-all flex items-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Contactar Soporte
            </a>
            <Link 
              to="/soporte/servicio-tecnico"
              className="px-6 py-3 bg-emerald-800/50 text-white font-semibold rounded-xl hover:bg-emerald-800 transition-all flex items-center gap-2"
            >
              Ver Servicio Técnico
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default GarantiasPage;
